import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Slide,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import React, { useEffect, useState } from "react";
import DateSelector from "../DateSelector";
import { uploadData } from "../../libs/firebase/firestore/uploadData";
import { errorCallback } from "../../libs/firebase/errorCallback";
import { successCallback } from "../../libs/firebase/successCallback";

const EventTypes = {
  PRUNE: "Prune",
  WATER: "Water",
  FERTILIZE: "Fertilize",
  MOVE: "Move",
  DEADHEAD: "Deadhead",
  REMOVE: "Remove",
  DIED: "Died",
  DEFOLIATE: "Defoliate",
  PEST: "Pest",
  CLEANUP_DEAD_BITS: "Cleanup Dead Bits",
};

function EventDescription({ value, onChange }) {
  return (
    <Box>
      <Typography variant="h6">Description</Typography>

      <TextField
        label=""
        multiline
        rows={2}
        variant="outlined"
        value={value}
        onChange={onChange}
        fullWidth
      />
    </Box>
  );
}

function EventTypeSelector({ selected, setSelected }) {
  /**
   * Select from MUI chips
   */

  const handleDelete = (chipToDelete) => () => {
    setSelected((chips) => chips.filter((chip) => chip !== chipToDelete));
  };

  const handleAdd = (chipToAdd) => () => {
    setSelected((chips) => [...chips, chipToAdd]);
  };

  const handleDeleteAll = () => {
    setSelected([]);
  };

  const handleSelect = (event) => {
    setSelected(event.target.value);
  };

  return (
    <Box>
      <Typography variant="h6">Event Type</Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        {Object.values(EventTypes).map((type) => {
          const isSelected = selected.includes(type);
          return (
            <Chip
              key={type}
              label={type}
              color={isSelected ? "primary" : "default"}
              onClick={handleAdd(type)}
              onDelete={isSelected ? handleDelete(type) : undefined}
            />
          );
        })}
        <Chip label="Delete All" color="error" onClick={handleDeleteAll} />
      </Box>
    </Box>
  );
}

function isDataInvalid(description, eventTypes, date) {
  // if date is in the future return an error
  if (date > new Date()) {
    return "Date cannot be in the future";
  }

  // if event types is empty or not an array return an error
  if (eventTypes.length === 0 || !Array.isArray(eventTypes)) {
    return "Event types must be an array and cannot be empty";
  }

  // if description is not a string return an error
  if (typeof description !== "string") {
    return "Description must be a string";
  }

  // if eventTypes contain move, remove, died, or pest then description cant be empty
  if (
    eventTypes.includes(EventTypes.MOVE) ||
    eventTypes.includes(EventTypes.REMOVE) ||
    eventTypes.includes(EventTypes.DIED) ||
    eventTypes.includes(EventTypes.PEST)
  ) {
    if (description === "") {
      return "Description cannot be empty";
    }
  }

  return false;
}

function dataToRequest(description, eventTypes, date, item, itemType) {
  return {
    description,
    type: eventTypes,
    timeDateActioned: date,
    timeDateCreated: new Date(),
    itemType: itemType,
    image: item.image,
    name: item.name,
    itemUid: item.uid,
  };
}

const AddEvent = ({
  open,
  onClose,
  item,
  siteUid,
  onAddEvent,
  title = "Event",
  itemType = "plant",
}) => {
  // state for description
  const [description, setDescription] = useState("");

  const [eventTypes, setEventTypes] = useState([]);

  const [date, setDate] = useState(new Date());

  const [dateOpen, setDateOpen] = useState(false);

  const error = isDataInvalid(description, eventTypes, date);

  const handleChooseDate = () => {
    setDateOpen(true);
  };

  useEffect(() => {}, []);
  const handleAddEvent = () => {
    // if siteUid is not defined return an error
    if (!siteUid) {
      console.error("siteUid is not defined");
      return;
    }
    // add event to firebase
    const request = dataToRequest(
      description,
      eventTypes,
      date,
      item,
      itemType
    );
    uploadData(
      request,
      `/sites/${siteUid}/events`,
      (...args) => {
        // clear all the fields
        setDescription("");
        setEventTypes([]);
        setDate(new Date());

        onAddEvent();
        successCallback(...args);
      },
      errorCallback
    );
  };

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Box
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: "73%",
          backgroundColor: "#fff",
          zIndex: 1300,
          overflowX: "auto",
          padding: "16px",
          display: "flex",
          // alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Stack direction={"row"}>
          <Typography variant="h5" style={{ marginBottom: "16px" }}>
            {title} for{" "}
            <span
              style={{
                fontWeight: "bold",
              }}
            >
              {item ? item.name : "..."}
            </span>
          </Typography>

          <div style={{ flexGrow: 1 }}></div>
          <Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Stack>
        {/* 
        Date selector
        Description box
        Event type selector
         */}
        <Stack spacing={2} direction={"column"}>
          <Chip
            onClick={handleChooseDate}
            label={date.toLocaleDateString("en-CA", {
              day: "numeric",
              month: "long",
            })}
            style={{ width: "fit-content" }}
          />
          {dateOpen && (
            <DateSelector
              date={date}
              onSelect={(date) => {
                setDateOpen(false);
                setDate(date);
              }}
            />
          )}
          <EventTypeSelector
            selected={eventTypes}
            setSelected={setEventTypes}
          />
          <EventDescription
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          {/* Show the error */}
          <Typography variant="body1" color="error">
            {error}
          </Typography>
          <Stack>
            <Button
              variant="contained"
              onClick={handleAddEvent}
              disabled={!!error}
            >
              Add Event
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Slide>
  );
};

export default AddEvent;
